/*
 * PDP v5 — loaded only on templates/product.product-v5.json
 *
 * Four jobs:
 *   1. the "?" disclosures in the stat grid and the spec table (one open at a
 *      time, per container)
 *   2. spec table tab switching, which also closes any open tooltip
 *   3. lazy attachment of reel video sources
 *   4. the mobile sticky add to cart, built here rather than by the shared
 *      script in layout/theme.liquid (see the section above syncStickyAtc)
 *
 * Self-contained on purpose: accordion-group.js does not load on this
 * template (main-product.liquid resets accordion_present before the secondary
 * block loop), so nothing here may assume it exists.
 *
 * There is no time or date logic anywhere in this file. The dispatch line is
 * a fixed string rendered server side. The estimated delivery line below it
 * does calculate a date, and lives in its own file (assets/pdp-delivery.js,
 * loaded by snippets/pdp-delivery.liquid) so that stays true of this one.
 */

const closeDisclosures = (scope) => {
  scope.querySelectorAll('.pdp-disclosure__toggle[aria-expanded="true"]').forEach((toggle) => {
    toggle.setAttribute('aria-expanded', 'false');
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (panel) panel.hidden = true;
  });
};

const initDisclosures = (root) => {
  root.querySelectorAll('[data-pdp-disclosure]').forEach((group) => {
    if (group.dataset.pdpReady === 'true') return;
    group.dataset.pdpReady = 'true';

    group.addEventListener('click', (event) => {
      const toggle = event.target.closest('.pdp-disclosure__toggle');
      if (!toggle || !group.contains(toggle)) return;

      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) return;

      const shouldOpen = toggle.getAttribute('aria-expanded') !== 'true';

      // One at a time.
      closeDisclosures(group);

      if (shouldOpen) {
        toggle.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
      }
    });
  });
};

const activateTab = (spec, tabs, target) => {
  // Switching tabs closes every open tooltip in the table, including ones in
  // panels that are about to be hidden.
  closeDisclosures(spec);

  tabs.forEach((tab) => {
    const isActive = tab === target;
    const panel = document.getElementById(tab.getAttribute('aria-controls'));

    tab.classList.toggle('is-active', isActive);
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    tab.tabIndex = isActive ? 0 : -1;
    if (panel) panel.hidden = !isActive;
  });
};

const initSpecTables = (root) => {
  root.querySelectorAll('[data-pdp-spec]').forEach((spec) => {
    if (spec.dataset.pdpReady === 'true') return;
    spec.dataset.pdpReady = 'true';

    const tabs = Array.from(spec.querySelectorAll('.pdp-spec__tab'));
    if (tabs.length < 2) return;

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activateTab(spec, tabs, tab));

      tab.addEventListener('keydown', (event) => {
        let nextIndex = null;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex === null) return;

        event.preventDefault();
        activateTab(spec, tabs, tabs[nextIndex]);
        tabs[nextIndex].focus();
      });
    });
  });
};

const attachReelSource = (video) => {
  if (video.dataset.pdpLoaded === 'true') return;

  const src = video.dataset.src;
  if (!src) return;

  const source = document.createElement('source');
  source.src = src;
  video.appendChild(source);
  video.dataset.pdpLoaded = 'true';
  video.load();
};

const initReels = (root) => {
  const videos = Array.from(root.querySelectorAll('[data-pdp-reel-video]')).filter(
    (video) => video.dataset.pdpReady !== 'true'
  );

  videos.forEach((video) => {
    video.dataset.pdpReady = 'true';
  });

  if (videos.length) {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            attachReelSource(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '200px 0px' }
      );

      videos.forEach((video) => observer.observe(video));
    } else {
      videos.forEach(attachReelSource);
    }
  }

  root.querySelectorAll('[data-pdp-reel-play]').forEach((button) => {
    if (button.dataset.pdpReady === 'true') return;
    button.dataset.pdpReady = 'true';

    button.addEventListener('click', () => {
      const card = button.closest('.pdp-reels__card');
      const video = card && card.querySelector('[data-pdp-reel-video]');
      if (!video) return;

      attachReelSource(video);
      card.classList.add('is-playing');

      const played = video.play();
      if (played && typeof played.catch === 'function') {
        played.catch(() => card.classList.remove('is-playing'));
      }
    });
  });
};

/*
 * Drop the gallery column so its top lines up with the first line of the
 * product title.
 *
 * The target is the title's first *line box*, not the top of the title block:
 * anything sitting above the title in the buy column (the label row, when the
 * template has one) is skipped, and so is the title's own half-leading. A
 * Range over the heading's contents gives that line box directly, which also
 * keeps the measurement correct when the title wraps or when the theme editor
 * changes the title size setting.
 *
 * This has to be measured rather than hardcoded. The title size is written
 * inline from the block settings, the face is a webfont, and the label row is
 * variant dependent (the <li> elements carry `hidden` and are toggled on
 * variant change), so the offset is not knowable at build time.
 *
 * The right hand column is absolutely positioned at the top of
 * .product-single__content, so the distance from that column's top to the
 * title's first line is exactly the offset the gallery needs. Setting it as a
 * margin on .product-single__media moves the whole column: the vertical
 * thumbnail rail, which is a grid track inside .media-gallery, and the badge
 * pills, which are absolutely positioned against .product-single__media
 * itself.
 *
 * Desktop only. Below 61.25em the layout is a single column and the gallery
 * sits above the buy box, where no offset applies.
 */
const DESKTOP_LAYOUT = '(min-width: 61.25em)';

// Top of the first rendered line of text, relative to the viewport. Falls
// back to the element box when the heading has no laid out text, which is the
// case for a product with an empty title in the editor.
const firstLineTop = (element) => {
  const range = document.createRange();
  range.selectNodeContents(element);
  const rects = range.getClientRects();
  range.detach();

  if (!rects.length) return element.getBoundingClientRect().top;

  let top = rects[0].top;
  for (let i = 1; i < rects.length; i += 1) {
    if (rects[i].top < top) top = rects[i].top;
  }
  return top;
};

const alignGalleryWithTitle = () => {
  document.querySelectorAll('.product-single').forEach((single) => {
    const media = single.querySelector('.product-single__media');
    const column = single.querySelector('.product-single__primary-blocks');
    const heading = single.querySelector(
      '.product-single__primary-blocks .product-single__box__block--title .product-single__title'
    );
    if (!media || !column || !heading) return;

    if (!window.matchMedia(DESKTOP_LAYOUT).matches) {
      media.style.marginTop = '';
      return;
    }

    // Clear first: the offset we set last time is above the title in the
    // same flow, so measuring without resetting would compound it on every
    // resize.
    media.style.marginTop = '';

    // Both elements live in the same (possibly sticky) subtree, so the
    // difference between them is stable no matter where the page is scrolled.
    const offset = Math.round(firstLineTop(heading) - column.getBoundingClientRect().top);

    media.style.marginTop = offset > 0 ? `${offset}px` : '';
  });
};

let alignFrame = null;

const scheduleGalleryAlignment = () => {
  if (alignFrame) cancelAnimationFrame(alignFrame);
  alignFrame = requestAnimationFrame(() => {
    alignFrame = null;
    alignGalleryWithTitle();
  });
};

// Registered once at module scope, not per init(), so repeated section loads
// in the theme editor do not stack up listeners.
window.addEventListener('resize', scheduleGalleryAlignment);
window.matchMedia(DESKTOP_LAYOUT).addEventListener('change', scheduleGalleryAlignment);

// Krona One is a webfont: the first line lands at a different height once it
// swaps in, so re-measure when the face is ready.
if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(scheduleGalleryAlignment);
}

// The label row appears and disappears with the selected variant, and the
// title itself reflows on a variant change that alters its width. Observing
// them directly is safe: neither depends on the gallery margin we set, so
// there is no feedback loop.
const alignObserver =
  'ResizeObserver' in window ? new ResizeObserver(scheduleGalleryAlignment) : null;

const watchGalleryAlignment = (root) => {
  if (alignObserver) {
    root
      .querySelectorAll(
        '.product-single__box__block--labels, .product-single__box__block--title'
      )
      .forEach((element) => {
        if (element.dataset.pdpAligned === 'true') return;
        element.dataset.pdpAligned = 'true';
        alignObserver.observe(element);
      });
  }
  scheduleGalleryAlignment();
};

/*
 * Sticky add to cart (mobile only).
 *
 * layout/theme.liquid carries the theme's own version of this -- it clones the
 * add-to-cart, hides the original and pins the clone to the foot of the
 * screen -- but it is wrapped in {% unless template.suffix == 'product-v5' %}
 * and so never runs here. That is deliberate and the two cannot coexist: the
 * script hides the very button this observer needs to watch, and it hides it
 * unconditionally, whether or not the shopper has scrolled anywhere near it.
 *
 * What replaces it: the real button stays where the buy column puts it, and a
 * separate pill is appended to <body> that shows only while that button is
 * off screen. The pill is a plain <button type="button"> that forwards its
 * click, not a clone -- a clone would carry name="add" and the form's submit
 * behaviour out of the form, which is what made the theme's version need the
 * preventDefault-then-click dance.
 *
 * Mobile only, and "only" is literal: above the breakpoint no element is
 * created, so there is nothing in the DOM and nothing for desktop CSS to
 * unset. Crossing the breakpoint builds or tears down rather than hiding.
 */

// Matches the media query in pdp-v5.css. Both must move together: a viewport
// in a gap between them gets an element the stylesheet does not style.
const STICKY_MEDIA = '(max-width: 47.9365em)';

// Scoped to .product-single so the quick shop modal and the cart drawer,
// which reuse .product-form__add-btn, cannot be picked up instead. Same scope
// the colour rule in pdp-v5.css uses.
const INLINE_ATC = '.product-single .product-form__add-btn';

const stickyMedia = window.matchMedia(STICKY_MEDIA);

let stickyButton = null;
let stickyInline = null;
let stickyVisibility = null;
let stickyMirror = null;

// The label lives in <staged-action-text>; the tick and spinner siblings are
// decoration and their text would come along with a plain textContent read.
const inlineAtcLabel = (inline) => {
  const text = inline.querySelector('.staged-action__text');
  return (text || inline).textContent.trim();
};

// Copies label and availability across. Reads the inline button and writes
// only to the pill, so observing the inline button to trigger this cannot
// loop.
const syncStickyFace = () => {
  if (!stickyButton || !stickyInline) return;
  stickyButton.textContent = inlineAtcLabel(stickyInline);
  stickyButton.disabled = stickyInline.disabled;
};

const teardownStickyAtc = () => {
  if (stickyVisibility) {
    stickyVisibility.disconnect();
    stickyVisibility = null;
  }
  if (stickyMirror) {
    stickyMirror.disconnect();
    stickyMirror = null;
  }
  if (stickyButton) {
    stickyButton.remove();
    stickyButton = null;
  }
  stickyInline = null;
  document.body.classList.remove('pdp-sticky-atc-space');
};

const buildStickyAtc = () => {
  // No observer, no pill. The fallback is the inline button, which is
  // untouched and works on its own; a pill that could never hide would be
  // worse than none.
  if (!('IntersectionObserver' in window)) return;

  const inline = document.querySelector(INLINE_ATC);
  if (!inline) return;

  const button = document.createElement('button');

  stickyInline = inline;
  stickyButton = button;

  // Explicitly type="button". It is outside the product form, but the pill is
  // appended to <body> and a future wrapper must not be able to make it a
  // submit.
  button.type = 'button';
  button.className = 'pdp-sticky-atc';

  // Forwarded rather than duplicated, so variant state, the staged-action
  // animation and any listener the theme or an app has attached all run once,
  // on the real button.
  button.addEventListener('click', () => {
    if (stickyInline) stickyInline.click();
  });

  syncStickyFace();
  document.body.appendChild(button);
  document.body.classList.add('pdp-sticky-atc-space');

  // The whole rule: visible exactly when the inline button is not. Threshold
  // 0 and no rootMargin, so the pill appears the moment the last pixel of the
  // inline button leaves and goes as soon as one pixel is back.
  stickyVisibility = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        button.classList.toggle('is-visible', !entry.isIntersecting);
      });
    },
    { threshold: 0 }
  );
  stickyVisibility.observe(inline);

  // A variant change rewrites the inline label and toggles its disabled
  // attribute without a section reload. Without this the pill can be left
  // reading ADD TO CART over a sold out variant, since it is only rebuilt on
  // a breakpoint change or a section load.
  if ('MutationObserver' in window) {
    stickyMirror = new MutationObserver(syncStickyFace);
    stickyMirror.observe(inline, {
      attributeFilter: ['disabled'],
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    });
  }
};

// Rebuild rather than reuse: a section load replaces the inline button, so a
// kept observer would be watching a node that is no longer in the document.
const syncStickyAtc = () => {
  teardownStickyAtc();
  if (stickyMedia.matches) buildStickyAtc();
};

// Registered once at module scope, not per init(), so repeated section loads
// in the theme editor do not stack up listeners.
stickyMedia.addEventListener('change', syncStickyAtc);

const init = (root = document) => {
  initDisclosures(root);
  initSpecTables(root);
  initReels(root);
  watchGalleryAlignment(root);
  syncStickyAtc();
};

init();

// Theme editor re-renders a section without a page load.
document.addEventListener('shopify:section:load', (event) => init(event.target));
